<template>
  <v-dialog
    v-model="dialog"
    max-width="1200px"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xs"
  >
    <v-card>
      <!-- Modal Header -->
      <v-card-title class="headline d-flex justify-space-between align-center pa-4">
        <div class="d-flex align-center">
          <v-icon left color="orange" class="mr-2">mdi-compare-horizontal</v-icon>
          <span class="text-h5 font-weight-bold">Detailed Vehicle Comparison</span>
        </div>
        <v-btn icon @click="closeModal" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Loading State -->
      <div v-if="loading" class="text-center pa-8">
        <v-progress-circular
          indeterminate
          color="orange"
          size="64"
          class="mb-4"
        ></v-progress-circular>
        <div class="text-h6 mb-2">Generating AI-Enhanced Comparison</div>
        <div class="text-subtitle-1 grey--text">
          SyncGuideX is analyzing vehicle features and specifications...
        </div>
      </div>

      <!-- Comparison Content -->
      <v-card-text v-else class="pa-0">
        <div class="comparison-container">
          <!-- Vehicle Images Row -->
          <div class="vehicle-images-row pa-4">
            <v-row>
              <v-col
                v-for="(vehicle, index) in vehicles"
                :key="getVehicleId(vehicle)"
                :cols="12 / Math.min(vehicles.length, 3)"
                class="text-center"
              >
                <div class="vehicle-image-container">
                  <!-- Colored Header Bar (Clickable) -->
                  <div
                    class="vehicle-header-bar clickable-header"
                    :style="{ backgroundColor: getVehicleColor(index) }"
                    @click="openVehicleDetailModal(vehicle, index)"
                  >
                    <h4 class="text-subtitle-1 font-weight-bold white--text ma-0 pa-2">
                      {{ getVehicleHeaderName(vehicle) }}
                    </h4>
                  </div>

                  <!-- Vehicle Image (Clickable) -->
                  <v-img
                    :src="getVehicleImage(vehicle)"
                    height="200"
                    class="rounded-bottom clickable-image"
                    @error="handleImageError"
                    @click="openVehicleDetailModal(vehicle, index)"
                  ></v-img>

                  <div class="vehicle-name mt-2">
                    <div class="text-h5 font-weight-bold" :style="{ color: getVehicleColor(index) }">
                      {{ currencyFormat(vehicle.amount) }}
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- Comparison Table -->
          <div class="comparison-table">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="comparison-parameter-header">
                      <v-icon left>mdi-format-list-bulleted</v-icon>
                      Comparison Parameters
                    </th>
                    <th
                      v-for="(vehicle, index) in vehicles"
                      :key="getVehicleId(vehicle)"
                      class="vehicle-column-header text-center"
                      :style="{ backgroundColor: getVehicleColor(index) + ' !important' }"
                    >
                      {{ getVehicleHeaderName(vehicle) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Basic Information -->
                  <tr class="category-header">
                    <td colspan="100%" class="category-title">
                      <v-icon left small>mdi-information</v-icon>
                      Basic Information
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Year</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-year'"
                      class="parameter-value text-center"
                    >
                      {{ vehicle.year || 'N/A' }}
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Make</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-make'"
                      class="parameter-value text-center"
                    >
                      {{ getVehicleMake(vehicle) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Model</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-model'"
                      class="parameter-value text-center"
                    >
                      {{ vehicle.model || 'N/A' }}
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Price</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-price'"
                      class="parameter-value text-center font-weight-bold"
                    >
                      {{ currencyFormat(vehicle.amount) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Mileage</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-mileage'"
                      class="parameter-value text-center"
                    >
                      {{ formatMileage(vehicle.mileage) }}
                    </td>
                  </tr>

                  <!-- Appearance -->
                  <tr class="category-header">
                    <td colspan="100%" class="category-title">
                      <v-icon left small>mdi-palette</v-icon>
                      Appearance
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Exterior Color</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-ext-color'"
                      class="parameter-value text-center"
                    >
                      {{ vehicle.exterior_color || 'N/A' }}
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Interior Color</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-int-color'"
                      class="parameter-value text-center"
                    >
                      {{ vehicle.interior_color || 'N/A' }}
                    </td>
                  </tr>

                  <!-- Location & Dealer -->
                  <tr class="category-header">
                    <td colspan="100%" class="category-title">
                      <v-icon left small>mdi-map-marker</v-icon>
                      Location & Dealer
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Location</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-location'"
                      class="parameter-value text-center"
                    >
                      {{ vehicle.location || 'N/A' }}
                    </td>
                  </tr>
                  <tr>
                    <td class="parameter-name">Dealer</td>
                    <td
                      v-for="vehicle in vehicles"
                      :key="getVehicleId(vehicle) + '-dealer'"
                      class="parameter-value text-center"
                    >
                      {{ vehicle.dealer_name || 'N/A' }}
                    </td>
                  </tr>

                  <!-- AI-Enhanced Features (if available) -->
                  <template v-if="comparisonData && comparisonData.ai_features">
                    <tr class="category-header">
                      <td colspan="100%" class="category-title">
                        <v-icon left small color="orange">mdi-robot</v-icon>
                        AI-Enhanced Features
                        <v-chip small color="orange" text-color="white" class="ml-2">
                          Powered by SyncGuideX
                        </v-chip>
                      </td>
                    </tr>
                    <tr
                      v-for="(feature, featureName) in comparisonData.ai_features"
                      :key="featureName"
                    >
                      <td class="parameter-name">{{ formatFeatureName(featureName) }}</td>
                      <td
                        v-for="vehicle in vehicles"
                        :key="getVehicleId(vehicle) + '-' + featureName"
                        class="parameter-value text-center"
                      >
                        {{ getFeatureValue(feature, getVehicleId(vehicle)) }}
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>
      </v-card-text>

      <!-- Modal Actions -->
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="orange"
          dark
          @click="closeModal"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Vehicle Detail Modal -->
    <vehicle-detail-modal
      v-if="selectedVehicleForDetail"
      v-model="showVehicleDetailModal"
      :vehicle="selectedVehicleForDetail"
      :column-color="selectedVehicleColumnColor"
      @close="closeVehicleDetailModal"
    />
  </v-dialog>
</template>

<script>
import Vue from 'vue';
import VehicleDetailModal from './VehicleDetailModal.vue';

export default {
  name: 'VehicleDetailedComparisonModal',
  components: {
    VehicleDetailModal
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    vehicles: {
      type: Array,
      required: true,
      default: () => []
    },
    comparisonData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      dialog: this.value,
      loading: false,
      showVehicleDetailModal: false,
      selectedVehicleForDetail: null,
      selectedVehicleColumnColor: 'orange'
    };
  },
  watch: {
    value(newVal) {
      this.dialog = newVal;
      if (newVal && !this.comparisonData) {
        this.loading = true;
        // Simulate loading time for AI processing
        setTimeout(() => {
          this.loading = false;
        }, 2000);
      }
    },
    dialog(newVal) {
      if (!newVal) {
        this.$emit('input', false);
        this.$emit('close');
        // Clear data when modal closes to prevent showing old data
        // But don't clear if vehicle detail modal is open
        if (!this.showVehicleDetailModal) {
          this.clearModalData();
        }
      }
    },
    comparisonData(newVal) {
      if (newVal) {
        this.loading = false;
      }
    },
    // Watch for changes in vehicles array to clear old data
    vehicles: {
      handler(newVehicles, oldVehicles) {
        // If vehicles array changes, clear the modal data
        if (newVehicles && oldVehicles && JSON.stringify(newVehicles) !== JSON.stringify(oldVehicles)) {
          this.clearModalData();
        }
      },
      deep: true
    }
  },
  methods: {
    closeModal() {
      this.dialog = false;
    },

    clearModalData() {
      // Reset loading state and clear any cached data
      this.loading = false;
      // Only clear vehicle detail modal data if it's not currently open
      if (!this.showVehicleDetailModal) {
        this.selectedVehicleForDetail = null;
        this.selectedVehicleColumnColor = 'orange';
      }
      // Emit event to parent to clear comparison data
      this.$emit('clear-data');
    },

    getVehicleId(vehicle) {
      return vehicle.id || `${vehicle.make}-${vehicle.model}-${vehicle.year}-${vehicle.amount}`;
    },

    getVehicleName(vehicle) {
      return vehicle.is_external && vehicle.title ?
        vehicle.title :
        `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    },

    // Get simplified header name (year and model only)
    getVehicleHeaderName(vehicle) {
      if (vehicle.is_external && vehicle.title) {
        // For external vehicles, try to extract year and model from title
        const title = vehicle.title;
        const parts = title.split(' ');

        // Try to find year and model
        let year = vehicle.year || '';
        let model = vehicle.model || '';

        // If we have year and model, use them
        if (year && model) {
          return `${year} ${model}`;
        }

        // Otherwise, try to extract from title (first 2-3 meaningful words)
        const meaningfulParts = parts.filter(part =>
          part.length > 2 &&
          !['used', 'certified', 'pre-owned', 'new'].includes(part.toLowerCase())
        );

        return meaningfulParts.slice(0, 3).join(' ') || title;
      }

      // For internal vehicles, use year and model
      return `${vehicle.year || ''} ${vehicle.model || ''}`.trim() || 'Vehicle';
    },

    getVehicleMake(vehicle) {
      // If vehicle has a direct make property, use it
      if (vehicle.make && vehicle.make !== 'N/A' && !vehicle.make.toLowerCase().includes('used')) {
        return vehicle.make;
      }

      // If vehicle has a title, try to extract make from it
      if (vehicle.title || (vehicle.is_external && vehicle.title)) {
        const title = vehicle.title || `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
        const titleParts = title.split(' ');

        // Skip common prefixes like "Used", "Certified", etc.
        const skipWords = ['used', 'certified', 'pre-owned', 'new'];
        let makeIndex = 0;

        for (let i = 0; i < titleParts.length; i++) {
          if (!skipWords.includes(titleParts[i].toLowerCase()) && isNaN(parseInt(titleParts[i]))) {
            makeIndex = i;
            break;
          }
        }

        // Return the make (skip year if it's the first part)
        if (titleParts.length > makeIndex + 1) {
          const potentialYear = parseInt(titleParts[makeIndex]);
          if (!isNaN(potentialYear) && potentialYear > 1900 && potentialYear <= new Date().getFullYear() + 1) {
            return titleParts[makeIndex + 1] || 'N/A';
          } else {
            return titleParts[makeIndex] || 'N/A';
          }
        }
      }

      return vehicle.make || 'N/A';
    },

    getVehicleImage(vehicle) {
      if (vehicle.images && vehicle.images.length > 0) {
        return vehicle.images[0].image || require('@/assets/img/coming-soon.png');
      }
      return require('@/assets/img/coming-soon.png');
    },

    handleImageError(event) {
      event.target.src = require('@/assets/img/coming-soon.jpg');
    },

    currencyFormat(value) {
      if (!value) return '$0';
      const roundedValue = Math.round(value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(roundedValue);
    },

    formatMileage(mileage) {
      if (!mileage || mileage === 'N/A') return 'N/A';
      if (typeof mileage === 'number') {
        return new Intl.NumberFormat('en-US').format(mileage) + ' mi';
      }
      return mileage;
    },

    formatFeatureName(featureName) {
      return featureName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    getFeatureValue(feature, vehicleId) {
      if (typeof feature === 'object' && feature[vehicleId]) {
        return feature[vehicleId];
      }
      return 'N/A';
    },

    // Get vehicle color based on index (Orange, Blue, Green)
    getVehicleColor(index) {
      const colors = ['#ff9800', '#2196f3', '#4caf50']; // Orange, Blue, Green
      return colors[index] || '#ff9800';
    },

    // Get vehicle color name for modal
    getVehicleColorName(index) {
      const colorNames = ['orange', 'blue', 'green'];
      return colorNames[index] || 'orange';
    },

    // Open vehicle detail modal
    openVehicleDetailModal(vehicle, index) {
      console.log('VehicleDetailedComparisonModal: Opening vehicle detail modal for:', vehicle);
      console.log('VehicleDetailedComparisonModal: Column index:', index);
      console.log('VehicleDetailedComparisonModal: Column color:', this.getVehicleColorName(index));

      this.selectedVehicleForDetail = vehicle;
      this.selectedVehicleColumnColor = this.getVehicleColorName(index);
      this.showVehicleDetailModal = true;

      // Emit event to parent to hide comparison footer
      this.$emit('vehicle-detail-opened');

      console.log('VehicleDetailedComparisonModal: Modal state set to:', this.showVehicleDetailModal);
    },

    // Close vehicle detail modal
    closeVehicleDetailModal() {
      console.log('VehicleDetailedComparisonModal: Closing vehicle detail modal');
      this.showVehicleDetailModal = false;
      this.selectedVehicleForDetail = null;
      this.selectedVehicleColumnColor = 'orange';

      // Emit event to parent to show comparison footer again
      this.$emit('vehicle-detail-closed');
    }

  }
};
</script>

<style scoped>
/* Modal styling */
.comparison-container {
  max-height: 80vh;
  overflow-y: auto;
}

.vehicle-images-row {
  background: #f5f5f5;
}

.vehicle-image-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.vehicle-image-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.vehicle-header-bar {
  border-radius: 8px 8px 0 0;
  transition: all 0.3s ease;
}

.clickable-header {
  cursor: pointer;
}

.clickable-header:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.clickable-image {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-image:hover {
  opacity: 0.95;
}

.vehicle-name {
  padding: 8px;
}

/* Comparison table styling */
.comparison-table {
  overflow-x: auto;
}

.comparison-parameter-header {
  background: #1976d2 !important;
  color: white !important;
  font-weight: 600 !important;
  min-width: 200px;
  position: sticky;
  left: 0;
  z-index: 2;
}

.vehicle-column-header {
  background: #ff9800 !important;
  color: white !important;
  font-weight: 600 !important;
  min-width: 150px;
}

.category-header {
  background: #e3f2fd !important;
}

.category-title {
  font-weight: 600 !important;
  color: #1976d2 !important;
  padding: 12px 16px !important;
}

.parameter-name {
  font-weight: 500;
  background: #fafafa;
  min-width: 200px;
  position: sticky;
  left: 0;
  z-index: 1;
  border-right: 1px solid #e0e0e0;
}

.parameter-value {
  padding: 8px 12px;
  border-left: 1px solid #e0e0e0;
}

.parameter-value:nth-child(even) {
  background: #f9f9f9;
}

/* Mobile responsive styles */
@media (max-width: 960px) {
  .comparison-container {
    max-height: 90vh;
  }

  .vehicle-column-header,
  .parameter-name {
    min-width: 120px;
    font-size: 0.875rem;
  }

  .parameter-value {
    font-size: 0.875rem;
    padding: 6px 8px;
  }

  .vehicle-name h3 {
    font-size: 1rem !important;
  }
}

@media (max-width: 600px) {
  .comparison-table {
    font-size: 0.75rem;
  }

  .vehicle-column-header,
  .parameter-name {
    min-width: 100px;
    font-size: 0.75rem;
  }

  .parameter-value {
    font-size: 0.75rem;
    padding: 4px 6px;
  }

  .vehicle-name h3 {
    font-size: 0.9rem !important;
  }

  .vehicle-name .text-h5 {
    font-size: 1rem !important;
  }
}

/* Scrollbar styling */
.comparison-container::-webkit-scrollbar,
.comparison-table::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.comparison-container::-webkit-scrollbar-track,
.comparison-table::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.comparison-container::-webkit-scrollbar-thumb,
.comparison-table::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.comparison-container::-webkit-scrollbar-thumb:hover,
.comparison-table::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
